/**
 * A project's menu (07 Workshop, row 48), on a tile and on a project in the
 * sidebar: Edit, Share…, Archive (Restore once archived) and Delete…, the last
 * the owner's alone and in the alert ink. A member sees who is in it. On a
 * phone the same actions rise as a sheet headed by the project.
 */
import { useState } from "react";
import { Button, Menu, MenuItem, MenuTrigger, Popover, type Key } from "react-aria-components";
import {
  Archive,
  ArchiveRestore,
  Ellipsis,
  Pencil,
  Trash2,
  Users,
  type LucideIcon,
} from "lucide-react";
import { KitModal } from "@/app/routes/dashboard/components/kit/Modal";
import { useCoShellPortal } from "@/app/routes/dashboard/CoShellPortalContext";
import { cn } from "@/utils/helpers";
import type { WorkshopProject } from "@/types/workshop.type";

export type ProjectMenuAction = "edit" | "share" | "people" | "archive" | "restore" | "delete";

type Item = { id: ProjectMenuAction; label: string; icon: LucideIcon; danger?: boolean };

/** What this viewer may do to this project, in menu order. */
export function projectMenuItems(project: WorkshopProject, canDelete: boolean): Item[] {
  // Sharing, settings and deletion are the owner's; a member sees who is in it.
  if (project.isOwner === false) {
    return [{ id: "people", label: "People in this project", icon: Users }];
  }
  const archived = project.status === "archived";
  return [
    ...(archived ? [] : [{ id: "edit" as const, label: "Edit", icon: Pencil }]),
    ...(archived ? [] : [{ id: "share" as const, label: "Share…", icon: Users }]),
    archived
      ? { id: "restore" as const, label: "Restore", icon: ArchiveRestore }
      : { id: "archive" as const, label: "Archive", icon: Archive },
    ...(canDelete ? [{ id: "delete" as const, label: "Delete…", icon: Trash2, danger: true }] : []),
  ];
}

type Props = {
  project: WorkshopProject;
  /** The project's owner: deleting it is theirs. */
  canDelete: boolean;
  /** Phone: the actions rise as a sheet instead of a menu. */
  asSheet: boolean;
  /** "3 CHATS · 8 OUTPUTS · ONLY YOU" under the sheet's title. */
  sheetMeta?: string;
  onAction: (action: ProjectMenuAction) => void;
  className?: string;
  onOpenChange?: (open: boolean) => void;
};

export default function WorkshopProjectMenu({
  project,
  canDelete,
  asSheet,
  sheetMeta,
  onAction,
  className,
  onOpenChange,
}: Props) {
  const portal = useCoShellPortal();
  const [sheetOpen, setSheetOpen] = useState(false);
  const items = projectMenuItems(project, canDelete);
  const label = `Project actions for ${project.name}`;

  if (asSheet) {
    const run = (action: ProjectMenuAction) => {
      setSheetOpen(false);
      onAction(action);
    };
    return (
      <>
        <button
          type="button"
          className={cn("ws-more", className)}
          aria-label={label}
          aria-haspopup="dialog"
          onClick={() => {
            onOpenChange?.(true);
            setSheetOpen(true);
          }}
        >
          <Ellipsis aria-hidden="true" />
        </button>
        <KitModal
          isOpen={sheetOpen}
          onOpenChange={(open) => {
            setSheetOpen(open);
            if (!open) onOpenChange?.(false);
          }}
          aria-label={label}
          hideCloseButton
          modalClassName="ws-dialog ws-action-sheet"
          overlayClassName="ws-dialog-overlay"
        >
          <span className="ws-sheet-grabber" aria-hidden="true" />
          <div className="ws-action-sheet-head">
            <p className="ws-action-sheet-title">{project.name}</p>
            {sheetMeta ? <p className="ws-action-sheet-meta">{sheetMeta}</p> : null}
          </div>
          <div className="ws-action-sheet-list">
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                className={cn("ws-action-sheet-item", item.danger && "is-danger")}
                onClick={() => run(item.id)}
              >
                <item.icon aria-hidden="true" />
                {item.label}
              </button>
            ))}
          </div>
        </KitModal>
      </>
    );
  }

  return (
    <MenuTrigger onOpenChange={onOpenChange}>
      <Button aria-label={label} className={cn("ws-more", className)}>
        <Ellipsis aria-hidden="true" />
      </Button>
      <Popover
        placement="bottom end"
        offset={6}
        UNSTABLE_portalContainer={portal ?? undefined}
        className="ws-menu-pop"
      >
        <Menu
          aria-label={label}
          className="ws-menu-list"
          onAction={(key: Key) => onAction(key as ProjectMenuAction)}
        >
          {items.map((item) => (
            <MenuItem key={item.id} id={item.id} textValue={item.label} className="ws-menu-row">
              <item.icon className="ws-menu-row-icon" aria-hidden="true" />
              <span className={cn("ws-menu-row-label", item.danger && "is-danger")}>{item.label}</span>
            </MenuItem>
          ))}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}
